import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, Radar, Bookmark, FileText, User, PlusCircle, LayoutDashboard, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isEmployer } from "@/lib/roles";

// Instagram-style bottom tab bar for mobile, with liquid glass.
export default function MobileTabBar() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  // Hide on welcome/auth routes and when logged out.
  const hiddenRoutes = ["/", "/login", "/register"];
  if (!user || hiddenRoutes.includes(location.pathname)) return null;

  const employer = isEmployer(user.role);

  const tabs = employer
    ? [
        { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
        { to: "/talent", icon: Users, label: "Talent" },
        { to: "/post-job", icon: PlusCircle, label: t("nav.postJob"), primary: true },
        { to: "/saved-candidates", icon: Bookmark, label: "Pool" },
        { to: "/profile", icon: User, label: t("nav.profile") },
      ]
    : [
        { to: "/radar", icon: Radar, label: t("nav.radar") },
        { to: "/jobs", icon: Briefcase, label: t("nav.jobs") },
        { to: "/saved", icon: Bookmark, label: t("nav.saved") },
        { to: "/applications", icon: FileText, label: t("nav.applications") },
        { to: "/profile", icon: User, label: t("nav.profile") },
      ];

  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-tab-bar">
      <div className="mx-3 mb-3 liquid-glass rounded-2xl px-2 py-1.5 flex items-center justify-around">
        {tabs.map((tab) => {
          const active = location.pathname === tab.to;
          if (tab.primary) {
            return (
              <Link key={tab.to} to={tab.to} data-testid={`tab-${tab.to.slice(1)}`}
                className="flex items-center justify-center -mt-6">
                <span className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-slate-900 shadow-lg shadow-teal-500/30 border-4 border-[#0A0E1A]">
                  <tab.icon size={22} />
                </span>
              </Link>
            );
          }
          return (
            <Link key={tab.to} to={tab.to} data-testid={`tab-${tab.to.slice(1)}`}
              className={`flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-xl transition-colors ${active ? "text-teal-300" : "text-slate-400"}`}>
              <tab.icon size={21} fill={active ? "currentColor" : "none"} strokeWidth={active ? 1.6 : 2} />
              <span className="text-[10px] font-medium">{tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
